/**
 * This middleware provides functionality for handling PKB (Personal Credit Bureau) query responses
 * and displaying appropriate snackbar notifications based on the results.
 * 
 * It automatically formats success and error messages using the application's internationalization system,
 * and allows for custom callback functions to be executed on success or error.
 * 
 * @module middleware/pkbOnQueryStarted
 */

import { openSnackbar } from 'store/slices/snackbar';
import { getIntl } from 'shared/utils/localesHelper';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { ReportResponse } from "features/compraPkb/types/report";
import {IntlShape} from "react-intl";


/**
 * Options for configuring the PKB snackbar middleware behavior.
 * 
 * @template TArg - The type of the argument passed to the query
 */
type PkbSnackbarOptions<TArg = any> = {
  /**
   * Message ID for success notification (used for i18n)
   */
  successMessage?: string;

  /**
   * Message ID for error notification (used for i18n)
   */
  errorMessage?: string;

  /**
   * Callback function executed when the query succeeds
   * 
   * @param {object} args - The success callback arguments
   * @param {TArg} args.arg - The original argument passed to the query
   * @param {any} args.result - The result returned by the query
   * @param {BaseQueryApi['dispatch']} args.dispatch - The Redux dispatch function
   */
  onSuccess?: (args: {
    arg: TArg;
    result: any;
    dispatch: BaseQueryApi['dispatch'];
  }) => void;

  /**
   * Callback function executed when the query fails
   * 
   * @param {object} args - The error callback arguments
   * @param {TArg} args.arg - The original argument passed to the query
   * @param {any} args.error - The error returned by the query
   * @param {BaseQueryApi['dispatch']} args.dispatch - The Redux dispatch function
   */
  onError?: (args: {
    arg: TArg;
    error: any;
    dispatch: BaseQueryApi['dispatch'];
  }) => void;
};

/**
 * Structure of a successful query result
 */
type Result = {
  /**
   * The data returned by the PKB report
   */
  data: ReportResponse,
}

/**
 * Builds a formatted message from the PKB report response
 * 
 * @param {IntlShape} intl - The internationalization object
 * @param {ReportResponse} data - The PKB report response data
 * @param {string} defaultMessageId - The default message ID to use if no specific message is available
 * 
 * @returns {string} The formatted message
 */
function buildMessage(intl: IntlShape, data: ReportResponse, defaultMessageId: string) {
  const messages: string[] = [];

  if (data.error) messages.push(data.error);
  if (data.message) messages.push(data.message);

  if (messages.length === 0) {
    return intl.formatMessage({ id: defaultMessageId });
  }

  const message = messages.join(': ');

  if (data.code) {
    return `${message} (${intl.formatMessage({ id: "pkbReportCode" }, { code: data.code })})`;
  }

  return message;
}

/**
 * Creates a middleware function that handles PKB query responses and displays appropriate snackbar notifications.
 * 
 * This middleware automatically shows success or error notifications based on the query result,
 * and allows for custom callback functions to be executed on success or error.
 * 
 * @template TArg - The type of the argument passed to the query
 * 
 * @param {PkbSnackbarOptions<TArg>} options - Configuration options for the middleware
 * @param {Function} [options.onSuccess] - Callback function executed when the query succeeds
 * @param {Function} [options.onError] - Callback function executed when the query fails
 * 
 * @returns {Function} A middleware function that can be used with RTK Query's `onQueryStarted` callback
 * 
 * @example
 * // In an API slice:
 * getPkbReport: builder.query<ReportResponse, string>({
 *   query: (id) => `/pkb/reports/${id}`,
 *   onQueryStarted: pkbOnQueryStarted({
 *     onSuccess: ({ result }) => {
 *       // Do something with the result
 *     }
 *   })
 * })
 */
export function pkbOnQueryStarted<TArg = any>({
  onSuccess,
  onError,
}: PkbSnackbarOptions<TArg>) {
  return async (arg: TArg, api: any) => {
    const { dispatch, queryFulfilled } = api;
    const intl = await getIntl();
    try {
      const result = await queryFulfilled;

      const { data }: Result = result;

      dispatch(
        openSnackbar({
          open: true,
          message: buildMessage(intl, data, "pkbReportSuccess"),
          variant: "alert",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          transition: "SlideLeft",
          autoHideDuration: 6000,
          alert: {
            color: "success",
          },
          close: false,
        })
      );

      onSuccess?.({ arg, result, dispatch });
    } catch (error) {
      const { error: { data: errorData } } = error as { error: Result };

      dispatch(
        openSnackbar({
          open: true,
          message: buildMessage(intl, errorData, "pkbReportError"),
          variant: "alert",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          transition: "SlideLeft",
          autoHideDuration: 6000,
          alert: {
            color: "error",
          },
          close: false,
        })
      );

      onError?.({ arg, error, dispatch });
    }
  };
}
